import { useState, useRef, useCallback } from 'react';
import apiService from '../services/apiService';

/**
 * Hook compartido para la lógica del carrito de compras
 * Usado por CounterSalesPage y NewSaleForm
 */
const useCart = ({ cliente, comisionTarjeta = 0, showToast }) => {
    const [cart, setCart] = useState([]);
    const [selectedCartItemId, setSelectedCartItemId] = useState(null);

    // Refs para timers de debounce
    const cartQuantityDebounceTimers = useRef({});
    const cartRef = useRef(cart);

    // Mantener cartRef actualizado
    cartRef.current = cart;

    /**
     * Obtiene el precio según la configuración del cliente
     */
    const getCustomerPrice = useCallback((producto) => {
        const getPrecioPreferido = () => {
            if (!cliente || !cliente.precio_venta) {
                return 'precio_lista';
            }
            return cliente.precio_venta;
        };

        const precioPreferido = getPrecioPreferido();
        let precio = producto[precioPreferido] || 0;

        if (precio === 0) {
            const preciosFallback = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
            for (const tipoPrecio of preciosFallback) {
                const precioAlternativo = producto[tipoPrecio] || 0;
                if (precioAlternativo > 0) {
                    precio = precioAlternativo;
                    break;
                }
            }
        }

        return precio;
    }, [cliente]);

    /**
     * Obtiene precio de una presentación/equivalencia según cliente
     */
    const getCustomerPriceFromPresentacion = useCallback((presentacion) => {
        const precioPreferido = cliente?.precio_venta || 'precio_lista';
        let precio = presentacion[precioPreferido] || 0;

        if (precio === 0) {
            const preciosFallback = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
            for (const tipoPrecio of preciosFallback) {
                const precioAlternativo = presentacion[tipoPrecio] || 0;
                if (precioAlternativo > 0) {
                    precio = precioAlternativo;
                    break;
                }
            }
        }
        return precio;
    }, [cliente]);

    /**
     * Carga equivalencias bidireccionales de un producto
     */
    const loadEquivalencias = useCallback(async (producto) => {
        try {
            const result = await apiService.getEquivalenciasBidireccionales(producto.id);
            if (result.success && result.data && result.data.length > 0) {
                const opciones = [
                    {
                        producto_id: producto.id,
                        nombre: producto.nombre,
                        codigo_barras: producto.codigo_barras,
                        codigo_corto: producto.codigo_corto,
                        unidad_medida: producto.unidad_medida || 'pz',
                        precio_lista: producto.precio_lista,
                        precio1: producto.precio1,
                        precio2: producto.precio2,
                        precio3: producto.precio3,
                        precio4: producto.precio4,
                        precio5: producto.precio5,
                        precio6: producto.precio6,
                        factor: 1,
                        es_producto_actual: true
                    },
                    ...result.data.map(eq => ({
                        producto_id: eq.producto_relacionado_id,
                        nombre: eq.producto_relacionado_nombre,
                        codigo_barras: eq.codigo_barras,
                        codigo_corto: eq.codigo_corto,
                        unidad_medida: eq.unidad_medida,
                        precio_lista: eq.precio_lista,
                        precio1: eq.precio1,
                        precio2: eq.precio2,
                        precio3: eq.precio3,
                        precio4: eq.precio4,
                        precio5: eq.precio5,
                        precio6: eq.precio6,
                        factor: eq.factor,
                        direccion: eq.direccion,
                        es_producto_actual: false
                    }))
                ];
                return opciones;
            }
        } catch (error) {
            console.error('Error cargando equivalencias:', error);
        }
        return [];
    }, []);

    /**
     * Elimina un item del carrito
     */
    const deleteFromCart = useCallback((productId) => {
        setCart(prevCart => prevCart.filter(item => item.id !== productId));
    }, []);

    /**
     * Actualiza cantidad con cálculo de descuento por volumen
     */
    const updateQuantityWithDiscount = useCallback(async (productId, newQuantity, showNotification = true) => {
        if (newQuantity <= 0) {
            deleteFromCart(productId);
            return;
        }

        const cantidadNum = parseFloat(newQuantity);

        // Actualizar cantidad inmediatamente en la UI
        setCart(prevCart => prevCart.map(cartItem =>
            cartItem.id === productId
                ? { ...cartItem, cantidad: cantidadNum }
                : cartItem
        ));

        // Pequeño delay para asegurar estado actualizado
        await new Promise(resolve => setTimeout(resolve, 50));

        const item = cartRef.current.find(i => i.id === productId);
        if (!item) return;

        // Determinar si debe calcular descuento
        const usaPrecioLista = !item.precio_tipo || item.precio_tipo === 'precio_lista';
        const tienePrecioLista = item.precios?.precio_lista > 0;

        if (usaPrecioLista && tienePrecioLista) {
            try {
                const result = await apiService.calcularPrecioConDescuento(productId, cantidadNum);
                if (result.success && result.data) {
                    const precioConDescuento = result.data.precio_final;
                    const descuentoAplicado = result.data.descuento_porcentaje || 0;
                    const descuentoAnterior = item.descuento_volumen || 0;

                    setCart(prevCart => prevCart.map(cartItem =>
                        cartItem.id === productId
                            ? {
                                ...cartItem,
                                precio_unitario: precioConDescuento,
                                descuento_volumen: descuentoAplicado
                            }
                            : cartItem
                    ));

                    // Notificaciones de descuento
                    if (showNotification && showToast && descuentoAplicado !== descuentoAnterior) {
                        if (descuentoAplicado > 0) {
                            showToast(`Descuento ${descuentoAplicado}% aplicado`, 'success');
                        } else if (descuentoAnterior > 0) {
                            showToast('Descuento removido', 'info');
                        }
                    }
                }
            } catch (error) {
                console.error('Error calculando descuento:', error);
            }
        }
    }, [deleteFromCart, showToast]);

    /**
     * Actualiza cantidad inmediatamente (para botones +/-)
     */
    const updateQuantity = useCallback((productId, newQuantity) => {
        updateQuantityWithDiscount(productId, newQuantity, true);
    }, [updateQuantityWithDiscount]);

    /**
     * Actualiza cantidad con debounce (para input)
     */
    const updateQuantityDebounced = useCallback((productId, newQuantity) => {
        const cantidadNum = parseFloat(newQuantity);

        // Actualizar UI inmediatamente
        setCart(prevCart => prevCart.map(cartItem =>
            cartItem.id === productId
                ? { ...cartItem, cantidad: isNaN(cantidadNum) ? 0 : cantidadNum }
                : cartItem
        ));

        // Cancelar timer anterior
        if (cartQuantityDebounceTimers.current[productId]) {
            clearTimeout(cartQuantityDebounceTimers.current[productId]);
        }

        // Programar cálculo con delay
        cartQuantityDebounceTimers.current[productId] = setTimeout(() => {
            if (cantidadNum > 0) {
                updateQuantityWithDiscount(productId, cantidadNum, true);
            } else if (cantidadNum <= 0) {
                deleteFromCart(productId);
            }
        }, 500);
    }, [updateQuantityWithDiscount, deleteFromCart]);

    /**
     * Disminuye cantidad en 1
     */
    const removeFromCart = useCallback((productId) => {
        const item = cart.find(item => item.id === productId);
        if (item && item.cantidad > 1) {
            const newQty = parseFloat(item.cantidad) - 1;
            updateQuantity(productId, newQty);
        } else {
            deleteFromCart(productId);
        }
    }, [cart, updateQuantity, deleteFromCart]);

    /**
     * Agrega producto directamente al carrito (sin modal de cantidad)
     */
    const addToCartDirectly = useCallback(async (producto) => {
        const precioUnitario = getCustomerPrice(producto);
        const existingItem = cart.find(item => item.producto_id === producto.id);

        if (existingItem) {
            const nuevaCantidad = existingItem.cantidad + 1;
            setCart(prevCart => prevCart.map(item =>
                item.producto_id === producto.id
                    ? { ...item, cantidad: nuevaCantidad }
                    : item
            ));
            setTimeout(() => {
                updateQuantityWithDiscount(producto.id, nuevaCantidad, true);
            }, 100);
        } else {
            // Cargar equivalencias
            const equivalencias = await loadEquivalencias(producto);
            const nuevoPrecioTipo = cliente?.precio_venta || 'precio_lista';

            setCart(prevCart => [...prevCart, {
                id: producto.id,
                producto_id: producto.id,
                producto_nombre: producto.nombre,
                nombre: producto.nombre,
                precio_unitario: precioUnitario,
                codigo_barras: producto.codigo_barras || '',
                codigo_corto: producto.codigo_corto || '',
                unidad_medida: producto.unidad_medida || 'pz',
                cantidad: 1,
                descuento: 0,
                impuesto: 0,
                impresora: producto.impresora || null,
                precios: {
                    precio_compra: producto.precio_compra || 0,
                    costo_precios: producto.costo_precios || 0,
                    precio_lista: producto.precio_lista || 0,
                    precio1: producto.precio1 || 0,
                    precio2: producto.precio2 || 0,
                    precio3: producto.precio3 || 0,
                    precio4: producto.precio4 || 0,
                    precio5: producto.precio5 || 0,
                    precio6: producto.precio6 || 0
                },
                precio_tipo: nuevoPrecioTipo,
                descuento_volumen: 0,
                equivalencias_disponibles: equivalencias.length > 1 ? equivalencias : []
            }]);

            // Recalcular descuento si usa precio_lista
            if (nuevoPrecioTipo === 'precio_lista' && producto.precio_lista > 0) {
                setTimeout(() => {
                    updateQuantityWithDiscount(producto.id, 1, true);
                }, 100);
            }
        }

        setSelectedCartItemId(producto.id);
    }, [cart, cliente, getCustomerPrice, loadEquivalencias, updateQuantityWithDiscount]);

    /**
     * Agrega producto al carrito con cantidad y precio calculado
     */
    const addToCartWithQuantity = useCallback((producto, cantidad, calculatedPricing, equivalenciasDisponibles) => {
        const cantidadNum = parseFloat(cantidad);
        if (isNaN(cantidadNum) || cantidadNum <= 0) return false;

        const precioUnitario = calculatedPricing?.precio_unitario || getCustomerPrice(producto);
        const existingItem = cart.find(item => item.id === producto.id);

        if (existingItem) {
            const nuevaCantidadTotal = existingItem.cantidad + cantidadNum;
            setCart(prevCart => prevCart.map(item =>
                item.id === producto.id
                    ? {
                        ...item,
                        cantidad: nuevaCantidadTotal,
                        precio_tipo: calculatedPricing?.precio_tipo || item.precio_tipo
                    }
                    : item
            ));
            setTimeout(() => {
                updateQuantityWithDiscount(producto.id, nuevaCantidadTotal, true);
            }, 100);
            setSelectedCartItemId(producto.id);
        } else {
            const nuevoPrecioTipo = cliente?.precio_venta || 'precio_lista';
            const precioLista = producto.precio_lista || 0;

            setCart(prevCart => [...prevCart, {
                id: producto.id,
                producto_id: producto.id,
                producto_nombre: producto.nombre,
                nombre: producto.nombre,
                precio_unitario: precioUnitario,
                codigo_barras: producto.codigo_barras || '',
                codigo_corto: producto.codigo_corto || '',
                unidad_medida: producto.unidad_medida || 'pz',
                cantidad: cantidadNum,
                descuento: 0,
                impuesto: 0,
                impresora: producto.impresora || null,
                precios: {
                    precio_compra: producto.precio_compra || 0,
                    costo_precios: producto.costo_precios || 0,
                    precio_lista: precioLista,
                    precio1: producto.precio1 || 0,
                    precio2: producto.precio2 || 0,
                    precio3: producto.precio3 || 0,
                    precio4: producto.precio4 || 0,
                    precio5: producto.precio5 || 0,
                    precio6: producto.precio6 || 0
                },
                precio_tipo: calculatedPricing?.precio_tipo || nuevoPrecioTipo,
                descuento_volumen: calculatedPricing?.descuento_aplicado || 0,
                equivalencias_disponibles: equivalenciasDisponibles?.length > 1 ? equivalenciasDisponibles : []
            }]);

            setSelectedCartItemId(producto.id);

            // Recalcular descuento si usa precio_lista
            if (nuevoPrecioTipo === 'precio_lista' && precioLista > 0) {
                setTimeout(() => {
                    updateQuantityWithDiscount(producto.id, cantidadNum, true);
                }, 100);
            }
        }

        return true;
    }, [cart, cliente, getCustomerPrice, updateQuantityWithDiscount]);

    /**
     * Cambia el nivel de precio de un item
     */
    const changePriceLevel = useCallback(async (productId, newPrice, precioTipo) => {
        const item = cart.find(i => i.id === productId);
        if (!item) return;

        // Determinar el precio_tipo si no se proporciona
        let nuevoPrecioTipo = precioTipo;
        if (!nuevoPrecioTipo && item.precios) {
            if (newPrice === item.precios.precio_lista) nuevoPrecioTipo = 'precio_lista';
            else if (newPrice === item.precios.precio1) nuevoPrecioTipo = 'precio1';
            else if (newPrice === item.precios.precio2) nuevoPrecioTipo = 'precio2';
            else if (newPrice === item.precios.precio3) nuevoPrecioTipo = 'precio3';
            else if (newPrice === item.precios.precio4) nuevoPrecioTipo = 'precio4';
            else if (newPrice === item.precios.precio5) nuevoPrecioTipo = 'precio5';
            else if (newPrice === item.precios.precio6) nuevoPrecioTipo = 'precio6';
        }

        // Si cambiamos A precio_lista, recalcular descuento
        if (nuevoPrecioTipo === 'precio_lista' && item.precios?.precio_lista > 0) {
            try {
                const result = await apiService.calcularPrecioConDescuento(productId, item.cantidad);
                if (result.success && result.data) {
                    const precioConDescuento = result.data.precio_final;
                    const descuentoAplicado = result.data.descuento_porcentaje || 0;

                    setCart(prevCart => prevCart.map(cartItem =>
                        cartItem.id === productId
                            ? {
                                ...cartItem,
                                precio_unitario: precioConDescuento,
                                precio_tipo: nuevoPrecioTipo,
                                descuento_volumen: descuentoAplicado
                            }
                            : cartItem
                    ));

                    if (showToast) {
                        if (descuentoAplicado > 0) {
                            showToast(`Precio Lista con ${descuentoAplicado}% descuento aplicado`, 'success');
                        } else {
                            showToast('Cambiado a Precio Lista', 'success');
                        }
                    }
                    return;
                }
            } catch (error) {
                console.error('Error calculando descuento:', error);
            }
        }

        // Si no es precio_lista o hubo error, actualizar sin descuento
        setCart(prevCart => prevCart.map(cartItem =>
            cartItem.id === productId
                ? { ...cartItem, precio_unitario: newPrice, precio_tipo: nuevoPrecioTipo, descuento_volumen: 0 }
                : cartItem
        ));

        if (showToast) {
            showToast('Precio actualizado', 'success');
        }
    }, [cart, showToast]);

    /**
     * Cambia la presentación/equivalencia de un item
     */
    const cambiarPresentacion = useCallback((itemId, nuevaPresentacion) => {
        const itemActual = cart.find(item => item.id === itemId);
        if (!itemActual) return;

        // Verificar si ya existe un item con la nueva presentación
        const itemExistente = cart.find(item =>
            item.producto_id === nuevaPresentacion.producto_id && item.id !== itemId
        );

        if (itemExistente) {
            // Agrupar: sumar cantidad al item existente y eliminar el actual
            const nuevaCantidad = itemExistente.cantidad + itemActual.cantidad;
            setCart(prevCart => prevCart
                .filter(item => item.id !== itemId)
                .map(item => {
                    if (item.producto_id === nuevaPresentacion.producto_id) {
                        return { ...item, cantidad: nuevaCantidad };
                    }
                    return item;
                })
            );
            setTimeout(() => {
                updateQuantityWithDiscount(nuevaPresentacion.producto_id, nuevaCantidad, true);
            }, 100);
        } else {
            // Cambiar presentación normalmente
            const nuevoPrecio = getCustomerPriceFromPresentacion(nuevaPresentacion);

            // Determinar el tipo de precio
            let nuevoPrecioTipo = cliente?.precio_venta || 'precio_lista';
            if (nuevoPrecio === nuevaPresentacion.precio_lista) nuevoPrecioTipo = 'precio_lista';
            else if (nuevoPrecio === nuevaPresentacion.precio1) nuevoPrecioTipo = 'precio1';
            else if (nuevoPrecio === nuevaPresentacion.precio2) nuevoPrecioTipo = 'precio2';
            else if (nuevoPrecio === nuevaPresentacion.precio3) nuevoPrecioTipo = 'precio3';
            else if (nuevoPrecio === nuevaPresentacion.precio4) nuevoPrecioTipo = 'precio4';
            else if (nuevoPrecio === nuevaPresentacion.precio5) nuevoPrecioTipo = 'precio5';
            else if (nuevoPrecio === nuevaPresentacion.precio6) nuevoPrecioTipo = 'precio6';

            setCart(prevCart => prevCart.map(item => {
                if (item.id === itemId) {
                    return {
                        ...item,
                        id: nuevaPresentacion.producto_id,
                        producto_id: nuevaPresentacion.producto_id,
                        producto_nombre: nuevaPresentacion.nombre,
                        nombre: nuevaPresentacion.nombre,
                        codigo_barras: nuevaPresentacion.codigo_barras || '',
                        codigo_corto: nuevaPresentacion.codigo_corto || '',
                        unidad_medida: nuevaPresentacion.unidad_medida || 'pz',
                        precio_unitario: nuevoPrecio,
                        precios: {
                            precio_lista: nuevaPresentacion.precio_lista || 0,
                            precio1: nuevaPresentacion.precio1 || 0,
                            precio2: nuevaPresentacion.precio2 || 0,
                            precio3: nuevaPresentacion.precio3 || 0,
                            precio4: nuevaPresentacion.precio4 || 0,
                            precio5: nuevaPresentacion.precio5 || 0,
                            precio6: nuevaPresentacion.precio6 || 0
                        },
                        precio_tipo: nuevoPrecioTipo,
                        factor_conversion: nuevaPresentacion.factor || 1
                    };
                }
                return item;
            }));
        }
    }, [cart, cliente, getCustomerPriceFromPresentacion, updateQuantityWithDiscount]);

    /**
     * Limpia el carrito
     */
    const clearCart = useCallback(() => {
        setCart([]);
        setSelectedCartItemId(null);
    }, []);

    /**
     * Calcula totales del carrito
     */
    const calculateTotals = useCallback(() => {
        let subtotal = 0;
        let descuento = 0;
        let impuesto = 0;
        let tienePrecioMayoreo = false;

        cart.forEach(item => {
            const itemSubtotal = item.cantidad * item.precio_unitario;
            subtotal += itemSubtotal;
            descuento += item.descuento || 0;
            impuesto += item.impuesto || 0;

            // Detectar si el producto usa precio de mayoreo
            const preciosMayoreo = ['precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
            if (item.precio_tipo && preciosMayoreo.includes(item.precio_tipo)) {
                tienePrecioMayoreo = true;
            }
        });

        const total = subtotal - descuento + impuesto;

        // Calcular total con comisión de tarjeta si hay productos con precio mayoreo
        let totalConComision = total;
        let montoComision = 0;
        if (tienePrecioMayoreo && comisionTarjeta > 0) {
            montoComision = total * (comisionTarjeta / 100);
            totalConComision = total + montoComision;
        }

        return { subtotal, descuento, impuesto, total, tienePrecioMayoreo, totalConComision, montoComision };
    }, [cart, comisionTarjeta]);

    // Calcular totales actuales
    const totals = calculateTotals();

    return {
        // Estado
        cart,
        setCart,
        selectedCartItemId,
        setSelectedCartItemId,

        // Funciones de precio
        getCustomerPrice,
        getCustomerPriceFromPresentacion,

        // Funciones del carrito
        addToCartDirectly,
        addToCartWithQuantity,
        removeFromCart,
        deleteFromCart,
        updateQuantity,
        updateQuantityWithDiscount,
        updateQuantityDebounced,
        changePriceLevel,
        cambiarPresentacion,
        clearCart,

        // Utilidades
        loadEquivalencias,
        calculateTotals,
        totals
    };
};

export default useCart;
